<template>
  <div class="theme-toggle-container">
    <!-- Simple Toggle Button -->
    <button
      v-if="simple"
      @click="handleThemeToggle()"
      class="theme-toggle-btn"
      :title="themeStore?.themeLabel || 'Toggle Theme'"
    >
      <i :class="themeStore?.themeIcon || 'fas fa-circle-half-stroke'" class="text-lg"></i>
    </button>

    <!-- Dropdown Menu -->
    <div v-else class="relative">
      <button
        @click="isOpen = !isOpen"
        class="theme-toggle-btn"
        :title="themeStore?.themeLabel || 'Theme Settings'"
      >
        <i :class="themeStore?.themeIcon || 'fas fa-circle-half-stroke'" class="text-lg"></i>
        <span v-if="showLabel" class="ml-2">{{ themeStore?.themeLabel || 'Theme' }}</span>
        <i class="fas fa-chevron-down ml-2 text-xs transition-transform" :class="{ 'rotate-180': isOpen }"></i>
      </button>

      <!-- Dropdown Menu -->
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="isOpen"
          class="absolute left-0 bottom-full mb-2 w-64 bg-white dark:bg-dark-800 rounded-lg shadow-xl border border-gray-200 dark:border-dark-600 z-50"
        >
          <div class="p-2">
            <div class="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider px-3 py-2">
              Theme Settings
            </div>
            <div
              v-for="option in themeOptions"
              :key="option.value"
              @click="selectTheme(option.value)"
              class="flex items-center px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              :class="{
                'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300': themeStore?.theme === option.value
              }"
            >
              <div class="flex items-center flex-1">
                <i :class="option.icon" class="w-5 text-center mr-3"></i>
                <div>
                  <div class="font-medium text-gray-900 dark:text-dark-100">{{ option.label }}</div>
                  <div class="text-sm text-gray-500 dark:text-dark-400">{{ option.description }}</div>
                </div>
              </div>
              <i
                v-if="themeStore?.theme === option.value"
                class="fas fa-check text-indigo-600 dark:text-indigo-400 ml-2"
              ></i>
            </div>
          </div>
        </div>
      </transition>

      <!-- Backdrop -->
      <div
        v-if="isOpen"
        @click="isOpen = false"
        class="fixed inset-0 z-40"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useThemeStore } from '../stores/theme'

const props = defineProps({
  simple: {
    type: Boolean,
    default: false
  },
  showLabel: {
    type: Boolean,
    default: false
  }
})

// Initialize theme store with error handling
let themeStore = null
try {
  themeStore = useThemeStore()
} catch (error) {
  console.error('Failed to initialize theme store:', error)
}

const isOpen = ref(false)

// Default theme options in case store fails
const defaultThemeOptions = [
  {
    value: 'light',
    label: 'Light Mode',
    icon: 'fas fa-sun',
    description: 'Always use light theme'
  },
  {
    value: 'dark',
    label: 'Dark Mode',
    icon: 'fas fa-moon',
    description: 'Always use dark theme'
  },
  {
    value: 'system',
    label: 'System Default',
    icon: 'fas fa-circle-half-stroke',
    description: 'Follow system preference'
  }
]

const themeOptions = computed(() => {
  return themeStore?.getThemeOptions() || defaultThemeOptions
})

const handleThemeToggle = () => {
  try {
    if (themeStore) {
      themeStore.toggleTheme()
    }
  } catch (error) {
    console.error('Failed to toggle theme:', error)
  }
}

const selectTheme = (theme) => {
  try {
    if (themeStore) {
      themeStore.setTheme(theme)
    }
    isOpen.value = false
  } catch (error) {
    console.error('Failed to select theme:', error)
    isOpen.value = false
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.theme-toggle-container')) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.theme-toggle-container {
  @apply inline-block;
}

.theme-toggle-btn {
  @apply flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all duration-200;
  @apply bg-gray-50 hover:bg-gray-100 text-gray-700;
  @apply dark:bg-dark-700 dark:hover:bg-dark-600 dark:text-dark-200;
  @apply focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400;
  @apply border border-gray-200 dark:border-dark-600;
  font-size: 0.875rem;
  font-weight: 500;
}

.theme-toggle-btn:hover {
  @apply transform scale-[1.02] shadow-md;
}
</style> 
